// The move gesture's rules (05e-2) — main.js's header drag, the pure parts: click vs
// drag, the group's fixed offsets, where a group lands, the edge auto-scroll speed and
// what a drop does. Unit-tested; useModuleMove.ts applies them to the page.

import { STEP } from "./placement.js";
import type { ModuleDropAction } from "./undo.js";

/** A header press only becomes a move once the pointer travels this far (px); released
 *  before that, it's a click that toggles the module's selection (Ben's #178). */
export const HEADER_CLICK_THRESHOLD = 4;

export function isDragStart(dx: number, dy: number): boolean {
  return Math.hypot(dx, dy) >= HEADER_CLICK_THRESHOLD;
}

/** A group member's fixed place relative to the module being dragged. */
export interface FollowerOffset {
  /** px gap from the leader — used every frame while dragging. */
  offsetX: number;
  offsetY: number;
  /** The same gap snapped to whole cells ONCE, at the press — used at the drop, so the
   *  group lands as one rigid block whatever pixel drift zoom caused. (Re-snapping each
   *  member on its own could round one to a different cell and break the arrangement.) */
  offsetCols: number;
  offsetRows: number;
}

export function followerOffset(leader: { left: number; top: number }, member: { left: number; top: number }): FollowerOffset {
  const offsetX = member.left - leader.left;
  const offsetY = member.top - leader.top;
  return { offsetX, offsetY, offsetCols: Math.round(offsetX / STEP), offsetRows: Math.round(offsetY / STEP) };
}

/** Where a group member lands: the leader's snapped cell plus its fixed cell offset. */
export function followerLanding(leaderLeft: number, leaderTop: number, f: FollowerOffset): { left: number; top: number } {
  return {
    left: (Math.round(leaderLeft / STEP) + f.offsetCols) * STEP,
    top: (Math.round(leaderTop / STEP) + f.offsetRows) * STEP,
  };
}

/** Near a screen edge (this many px) a drag scrolls the page toward it. */
export const AUTO_SCROLL_ZONE = 80;
/** px per frame, right at the edge. */
export const AUTO_SCROLL_MAX = 14;

/**
 * main.js's edge auto-scroll speed on one axis (negative = toward the start). It only
 * scrolls toward an edge the pointer is moving toward (or holding still at): otherwise,
 * moving back out from the edge, the scroll would keep going and fight the pointer.
 */
export function autoScrollSpeed(pos: number, size: number, movingToStart: boolean, movingToEnd: boolean): number {
  if (pos < AUTO_SCROLL_ZONE && !movingToEnd) {
    return -Math.pow((AUTO_SCROLL_ZONE - pos) / AUTO_SCROLL_ZONE, 1.5) * AUTO_SCROLL_MAX;
  }
  if (pos > size - AUTO_SCROLL_ZONE && !movingToStart) {
    return Math.pow((pos - (size - AUTO_SCROLL_ZONE)) / AUTO_SCROLL_ZONE, 1.5) * AUTO_SCROLL_MAX;
  }
  return 0;
}

/** What releasing a header press does. */
export type MoveDrop =
  /** The group lands together (or goes back, if invalid or a click). */
  | { kind: "group"; invalid: boolean }
  /** A group dropped on a Radar / Stats tile some of its players join: they're added, and the
   *  group goes back where it was (Ben, 2026-11-02). */
  | { kind: "group-join" }
  /** Dropped onto a module it combines with — main.js's action runs (05g). */
  | { kind: ModuleDropAction }
  /** Snaps into the free cell under it. */
  | { kind: "place" }
  /** Back where it started (the red outline, or a header click). */
  | { kind: "revert" };

/**
 * main.js's drop decision. A header click (never moved past the threshold) counts as an
 * invalid drop — everything goes back, then the click toggles the selection. Groups never
 * merge or switch, except that their players join a Radar / Stats tile; a single module does
 * when it was dropped onto one it combines with.
 */
export function moveDrop(opts: {
  wasHeaderClick: boolean;
  isGroup: boolean;
  indicatorInvalid: boolean;
  /** What the module under the drop would do with this one (null: nothing / not looked). */
  dropAction: ModuleDropAction | null;
  /** A group: the held module landed on a Radar / Stats tile at least one member's player joins. */
  groupJoins?: boolean;
}): MoveDrop {
  const invalid = opts.wasHeaderClick || opts.indicatorInvalid;
  if (opts.isGroup) return opts.groupJoins && !opts.wasHeaderClick ? { kind: "group-join" } : { kind: "group", invalid };
  if (opts.dropAction && !opts.wasHeaderClick) return { kind: opts.dropAction };
  return invalid ? { kind: "revert" } : { kind: "place" };
}
